import { useEffect, useState } from "react";
import { Calout } from "@/ui/Calout";
import { Modal } from "@/ui/Modal";
import { PopupSizes } from "@/ui/Modal/types";
import { createPage, getPage, updatePage, makeSlug, publishPage } from "@/api/pages";
import { CodeView } from "./CodeView";
import { getCampaign } from "@/api/campaigns";
import { pageIdBySlug, pageTooBig } from "./dropPublish";
import { getSessionToken } from "@/api/auth";

// AMB-695 — add a page to a site by pasting its code, or update an existing one.
// The page is saved as a draft (type "html", code in htmlContent); nothing goes live
// until someone publishes it. Full address checks arrive with AMB-696.

const titleOf = (html: string) => {
  const match = /<title[^>]*>([^<]*)<\/title>/i.exec(html || "");
  return match ? match[1].trim() : "";
};

interface PasteHtmlModalProps {
  setIsOpen: (isOpen: boolean) => void;
  campaignId: string;
  siteAddress?: string; // e.g. pages.first-bank.net
  takenSlugs?: string[]; // addresses already used by this site's other pages
  pageId?: string; // set when updating an existing HTML page
  onSaved?: () => void;
}

export const PasteHtmlModal: React.FC<PasteHtmlModalProps> = ({
  setIsOpen,
  campaignId,
  siteAddress = "•••",
  takenSlugs = [],
  pageId,
  onSaved,
}) => {
  const [code, setCode] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [slug, setSlug] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [saving, setSaving] = useState(false);

  const isUpdate = Boolean(pageId);

  useEffect(() => {
    if (!pageId) return;
    (async () => {
      const response = await getPage(pageId, { token: getSessionToken() });
      const page = response?.data;
      if (page) {
        setCode(page.htmlContent || "");
        setName(page.name || "");
        setSlug(page.slug || "");
      }
    })();
  }, [pageId]);

  const pageName = name || titleOf(code);
  const pageSlug = makeSlug(slug || pageName || "");
  const slugTaken = !isUpdate && takenSlugs.includes(pageSlug);

  const handleSave = async () => {
    setError("");
    if (saving) return;
    if (!code.trim()) return setError("Paste the page's code first.");
    const big = pageTooBig(code);
    if (big) return setError(big);
    if (!pageName) return setError("Give the page a name.");
    if (!pageSlug) return setError("Give the page an address.");
    if (slugTaken) return setError(`Another page in this site already uses /${pageSlug}.`);

    const token = getSessionToken();
    setSaving(true);
    const response = isUpdate
      ? await updatePage(pageId, { token, settings: { name: pageName, htmlContent: code } })
      : await createPage({
          token,
          settings: {
            campaignId,
            name: pageName,
            slug: pageSlug,
            type: "html",
            theme: "html",
            content: [],
            htmlContent: code,
          },
        });

    if (!response || response.error) {
      setSaving(false);
      return setError(response?.error || "The page couldn't be saved. Try again.");
    }
    // Round 2 (D16): a new pasted page goes live right away, like a dropped file
    if (!isUpdate) {
      const newId = pageIdBySlug(await getCampaign({ token, id: campaignId }), pageSlug);
      const pub: any = newId ? await publishPage(newId, { token }) : null;
      if (!pub || pub.error || pub.ok === false) {
        setSaving(false);
        if (onSaved) onSaved();
        return setError(`Saved as a draft, but it didn't go live: ${pub?.error || "open the page and click Publish"}.`);
      }
    }
    setSaving(false);
    setIsOpen(false);
    if (onSaved) onSaved();
  };

  const fieldClass =
    "w-full rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-4 py-3 text-black dark:text-white placeholder:text-black/30 dark:placeholder:text-white/30 focus:outline-none focus:border-black/30";

  return (
    <Modal
      title={isUpdate ? `Update "${pageName || "page"}"` : "Paste a page"}
      onClose={() => setIsOpen(false)}
      onSuccess={handleSave}
      successCaption={saving ? (isUpdate ? "Saving…" : "Publishing…") : isUpdate ? "Save changes" : "Publish"}
      size={PopupSizes.LARGE}
    >
      <div className="flex flex-col gap-5 text-left">
        <div>
          <label className="block text-xs text-black/50 dark:text-white/50 mb-1.5">Page code</label>
          <CodeView value={code} onChange={setCode} height={300} autoFocus />
          {!code && <p className="mt-1.5 text-xs text-black/40 dark:text-white/40">Paste the whole page Claude gave you, starting with &lt;!doctype html&gt;.</p>}
        </div>
        <div className={`grid gap-4 ${isUpdate ? "" : "sm:grid-cols-2"}`}>
          <div>
            <label className="block text-xs text-black/50 dark:text-white/50 mb-1.5">Page name</label>
            <input
              className={fieldClass}
              placeholder={titleOf(code) || "Taken from the page's title"}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {!isUpdate && (
            <div>
              <label className="block text-xs text-black/50 dark:text-white/50 mb-1.5">Address</label>
              <div className={`${fieldClass} flex items-center`}>
                <span className="text-black/40 dark:text-white/40 whitespace-nowrap">/</span>
                <input
                  className="w-full bg-transparent focus:outline-none placeholder:text-black/30 dark:placeholder:text-white/30"
                  style={{ padding: 0, border: 0, marginLeft: 2 }}
                  placeholder={pageSlug || "october-event"}
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
        <div className="text-sm text-black/60 dark:text-white/60">
          Goes live at{" "}
          <span className="font-medium text-black dark:text-white break-all">
            {siteAddress}/{pageSlug || "…"}
          </span>{" "}
          {isUpdate ? "when you publish. Saving updates the draft only." : "as soon as you click Publish."}
        </div>
        {error && <Calout title={error} />}
      </div>
    </Modal>
  );
};
